// src/services/supabaseClient.js
import { processLock } from "@supabase/auth-js";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const STAGING_SUPABASE_URL = "https://nwgxdrldipcuyxupaqit.supabase.co";
const STAGING_SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im53Z3hkcmxkaXBjdXl4dXBhcWl0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg5MTIxMjksImV4cCI6MjA4NDQ4ODEyOX0.o_2aKcb26fr0WB1yT2uNA63-iOemYXpVlOQuEsMJ9Mc";
const LOCAL_PROOF_UNAVAILABLE_URL = "http://127.0.0.1:54321";
const LOCAL_PROOF_UNAVAILABLE_KEY = "local-proof-services-not-ready";

function getRuntimeHostname() {
  if (typeof window === "undefined") return "";
  return String(window.location.hostname || "").trim().toLowerCase();
}

function getRuntimePort() {
  if (typeof window === "undefined") return "";
  return String(window.location.port || "").trim();
}

function isLocalRuntimeHost(hostname = getRuntimeHostname()) {
  const normalized = String(hostname || "").replace(/^\[|\]$/g, "");
  if (normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1") return true;
  const parts = normalized.split(".");
  if (parts.length !== 4 || parts.some((part) => !/^\d{1,3}$/.test(part) || Number(part) > 255)) return false;
  const [first, second] = parts.map(Number);
  return first === 10 || (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168);
}

function isLocalSupabaseUrl(url) {
  try {
    const parsed = new URL(String(url || ""));
    return parsed.protocol === "http:" && ["localhost", "127.0.0.1", "::1"].includes(parsed.hostname);
  } catch {
    return false;
  }
}

function isStagingHost(hostname = getRuntimeHostname()) {
  return (
    hostname === "staging.afrimed.tech" ||
    hostname.endsWith("--afrimed-staging.netlify.app") ||
    hostname.endsWith("--afrimed-tech.netlify.app")
  );
}

export function isExplicitLocalStagingUiMode(hostname = getRuntimeHostname()) {
  return (
    isLocalRuntimeHost(hostname) &&
    getRuntimePort() === "4028" &&
    String(import.meta.env.VITE_AFRIMED_LOCAL_MODE || "").trim().toLowerCase() === "staging-ui"
  );
}

export function getSupabaseRuntimeConfig() {
  if (isStagingHost()) {
    return {
      url: STAGING_SUPABASE_URL,
      anonKey: STAGING_SUPABASE_ANON_KEY,
      source: "runtime-staging-override",
    };
  }

  // Port 4028 is an intentional, clearly marked staging-connected local UI
  // mode. Its target is selected here by the launcher flag, never by Vite's
  // .env.local precedence.
  if (isExplicitLocalStagingUiMode()) {
    return {
      url: STAGING_SUPABASE_URL,
      anonKey: STAGING_SUPABASE_ANON_KEY,
      source: "explicit-local-staging-ui",
    };
  }

  const buildConfig = {
    url: SUPABASE_URL,
    anonKey: SUPABASE_ANON_KEY,
    source: "build-env",
  };

  // A localhost app must never quietly authenticate against a hosted project
  // merely because a developer's .env.local has higher Vite precedence. The
  // local-proof launcher injects the active local API URL and publishable key;
  // every other localhost configuration fails closed instead.
  if (isLocalRuntimeHost() && !isLocalSupabaseUrl(buildConfig.url)) {
    return { url: "", anonKey: "", source: "blocked-local-remote-config" };
  }

  return buildConfig;
}

export function getSupabaseBaseUrl() {
  return String(getSupabaseRuntimeConfig().url || "").trim().replace(/\/+$/, "");
}

export function getSupabaseAnonKey() {
  return String(getSupabaseRuntimeConfig().anonKey || "").trim();
}

function resolveRuntimeAppUrl() {
  const runtimeOrigin =
    typeof window !== "undefined" ? String(window.location.origin || "").trim() : "";
  const runtimeHost =
    typeof window !== "undefined" ? String(window.location.hostname || "").trim().toLowerCase() : "";

  if (
    runtimeHost === "localhost" ||
    runtimeHost === "127.0.0.1" ||
    runtimeHost === "::1" ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(runtimeHost)
  ) {
    return (runtimeOrigin || "http://localhost:4028").replace(/\/+$/, "");
  }

  if (isStagingHost()) {
    return "https://staging.afrimed.tech";
  }

  const appEnv = String(import.meta.env.VITE_APP_ENV || "").trim().toLowerCase();
  if (appEnv === "staging") {
    return "https://staging.afrimed.tech";
  }

  const envUrl = String(import.meta.env.VITE_APP_URL || "").trim();
  if (envUrl) return envUrl.replace(/\/+$/, "");

  if (runtimeOrigin) return runtimeOrigin.replace(/\/+$/, "");

  return "http://localhost:4028";
}

const runtimeConfig = getSupabaseRuntimeConfig();
const hasRuntimeConfig = Boolean(runtimeConfig.url && runtimeConfig.anonKey);

// Keep module imports safe when the isolated local proof stack is not ready.
// App renders its explicit readiness screen before any provider can use this
// inert loopback client, so localhost never falls through to staging.
const supabase = createClient(
  hasRuntimeConfig ? runtimeConfig.url : LOCAL_PROOF_UNAVAILABLE_URL,
  hasRuntimeConfig ? runtimeConfig.anonKey : LOCAL_PROOF_UNAVAILABLE_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // AuthCallback handles all callback/session exchange explicitly.
      // Keeping this false avoids double-processing callback payloads.
      detectSessionInUrl: false,
      // Use the implicit flow for email magic links so first-use links do not
      // depend on a fragile PKCE code-verifier state surviving mail-app/browser hops.
      flowType: "implicit",
      // Safari's navigator LockManager path has been stalling session writes during
      // magic-link callbacks. A process-local lock preserves serialization in-tab
      // without the cross-tab lock-manager delays.
      lock: processLock,
    },
  },
);

export function getRedirectUrl(path = "/auth/callback") {
  const safePath = `/${String(path || "/auth/callback").replace(/^\/+/, "")}`;
  return `${resolveRuntimeAppUrl()}${safePath}`;
}

export default supabase;
export { supabase };
